import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { ServiceStandardsListResult } from "@/modules/service-standard/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CopyIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
} from "lucide-react";

type ServiceStandardRow = ServiceStandardsListResult["data"][number];

type ServiceStandardTableProps = {
  data: ServiceStandardRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit?: (standard: ServiceStandardRow) => void;
  onView?: (id: string) => void;
  onCopy?: (id: string) => void;
  onActivate?: (standard: ServiceStandardRow) => void;
  onDeactivate?: (standard: ServiceStandardRow) => void;
  onDelete?: (standard: ServiceStandardRow) => void;
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  draft: "secondary",
  inactive: "destructive",
};

// Color mapping for service channels (different colors for different channels)
const getServiceChannelColor = (
  channelName: string | null,
): "default" | "secondary" | "outline" | "destructive" => {
  if (!channelName) return "outline";
  // You can customize colors based on channel names
  const channelColors: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    ຕ້ອນຮັບຫນ້າຮ້ານ: "default",
    ແຊັດອອນລາຍ: "secondary",
    "Live ຂາຍ": "outline",
  };
  return channelColors[channelName] || "outline";
};

export function ServiceStandardTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onCopy,
  onActivate,
  onDeactivate,
  onDelete,
}: ServiceStandardTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<ServiceStandardRow>[] = [
    createSortableColumn<ServiceStandardRow>("name", "ຊື່ມາດຕະຖານ", {
      size: 200,
    }),
    {
      id: "serviceChannelName",
      header: "ປະເພດບໍລິການ",
      cell: ({ row }) => (
        <Badge
          variant={getServiceChannelColor(row.original.serviceChannelName)}
        >
          {row.original.serviceChannelName || "-"}
        </Badge>
      ),
      size: 150,
    },
    {
      id: "departmentName",
      header: "ຜະແນກທີ່ໃຊ້",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<ServiceStandardRow>("updatedAt", "ວັນທີ່ອັບເດດລ່າສຸດ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.updatedAt)}
        </span>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: ServiceStandardRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has(Permissions.service_standard.read) && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        if (has(Permissions.service_standard.update) && onEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.service_standard.update)) {
          const isActive = row.original.status === "active";
          if (isActive && onDeactivate) {
            actions.push({
              label: "ປິດໃຊ້ງານ",
              icon: <EyeOffIcon className="h-4 w-4" />,
              onClick: () => onDeactivate(row.original),
            });
          } else if (!isActive && onActivate) {
            actions.push({
              label: "ເປີດໃຊ້ງານ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onActivate(row.original),
            });
          }
        }

        if (has(Permissions.service_standard.create) && onCopy) {
          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: () => onCopy(row.original.id),
          });
        }

        if (has(Permissions.service_standard.delete) && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const standard = row.original;
              const ok = await confirm({
                title: "ລຶບມາດຕະຖານບໍລິການ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${standard.name}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(standard);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="service-standard-table">
      <DataTable<ServiceStandardRow, unknown>
        noDataMessage="ບໍ່ພົບມາດຕະຖານບໍລິການ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
